import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';
import { qualificationApi } from '../qualificationApi';
import MoneyInput from '../shared/MoneyInput';
import NumberInput from '../shared/NumberInput';

const TERM_OPTIONS = [
  { v: 360, l: '30 years (360 mo)' },
  { v: 240, l: '20 years (240 mo)' },
  { v: 180, l: '15 years (180 mo)' },
];

export default function QualificationDefaultsTab() {
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    qualificationApi.getDefaults()
      .then(setDefaults)
      .catch((e) => toast({ title: 'Could not load defaults', description: errorMessage(e) }));
  }, []);

  const set = (patch) => setDefaults((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const updated = await qualificationApi.updateDefaults(defaults);
      setDefaults(updated);
      toast({ title: 'Defaults saved' });
    } catch (e) {
      toast({ title: 'Could not save defaults', description: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  if (!defaults) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400" data-testid="qual-defaults-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading...
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 max-w-lg space-y-4" data-testid="qual-defaults-tab">
      <div>
        <Label className="text-xs text-slate-500">Property Tax Rate</Label>
        <NumberInput value={defaults.property_tax_rate} suffix="%" onChange={(n) => set({ property_tax_rate: n ?? 0 })} className="mt-1" data-testid="qual-defaults-tax-rate" />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Insurance Rate</Label>
        <NumberInput value={defaults.insurance_rate} suffix="%" onChange={(n) => set({ insurance_rate: n ?? 0 })} className="mt-1" data-testid="qual-defaults-insurance-rate" />
      </div>
      <div>
        <Label className="text-xs text-slate-500">HOA (monthly)</Label>
        <MoneyInput value={defaults.hoa_monthly} onChange={(n) => set({ hoa_monthly: n ?? 0 })} className="mt-1" data-testid="qual-defaults-hoa" />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Term</Label>
        <Select value={String(defaults.term_months)} onValueChange={(v) => set({ term_months: Number(v) })}>
          <SelectTrigger className="mt-1" data-testid="qual-defaults-term"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TERM_OPTIONS.map((t) => <SelectItem key={t.v} value={String(t.v)}>{t.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <button type="button" onClick={save} disabled={saving} className="btn-gold" data-testid="qual-defaults-save">
        {saving ? <Loader2 className="animate-spin" size={14} /> : null} Save
      </button>
    </div>
  );
}
